/**
 * Audience targeting block for coupons. Used in both the new-coupon form and
 * the edit-coupon modal in the Admin Dashboard.
 *
 *   value: { audience, audience_promotion_id, audience_user_email }
 *   onChange: (partialUpdate) => void  // shallow merge into parent state
 *   promotions: [{id,title}]           // for the promotion dropdown
 *   testIdPrefix: "new-coupon" | "edit-coupon"
 */
export default function CouponAudienceFields({ value = {}, onChange, promotions = [], testIdPrefix = "coupon-audience" }) {
    const audience = value.audience || "all";

    const options = [
        { id: "all", label: "Everyone", help: "Visible to all visitors (default)." },
        { id: "new_customers", label: "New customers only", help: "Users with 0 completed bookings." },
        { id: "returning_customers", label: "Returning customers only", help: "Users with at least 1 completed booking." },
        { id: "promotion", label: "Customers from a specific promotion", help: "Visible only to users who arrived through the selected promotion." },
        { id: "single_user", label: "Reserved for one user (by email)", help: "VIP / apology / personal coupon for one customer." },
    ];

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-audience`}>
            <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[#7A5262]">Visible to</div>
            <select
                value={audience}
                onChange={(e) => onChange({ audience: e.target.value, audience_promotion_id: null, audience_user_email: "" })}
                className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                data-testid={`${testIdPrefix}-audience-select`}
            >
                {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <p className="text-[0.6rem] text-[#8A6574]">{options.find((o) => o.id === audience)?.help}</p>

            {audience === "promotion" && (
                <select
                    value={value.audience_promotion_id || ""}
                    onChange={(e) => onChange({ audience_promotion_id: e.target.value || null })}
                    className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none mt-2"
                    data-testid={`${testIdPrefix}-audience-promotion`}
                >
                    <option value="">Select a promotion…</option>
                    {promotions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
            )}

            {audience === "single_user" && (
                <input
                    type="email"
                    placeholder="customer@email.com"
                    value={value.audience_user_email || ""}
                    onChange={(e) => onChange({ audience_user_email: e.target.value })}
                    className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none mt-2"
                    data-testid={`${testIdPrefix}-audience-email`}
                />
            )}
        </div>
    );
}
